// OWNER: tracking agent. Pure helpers for the Team tracking section of Reports (SPEC §19, 2026-10-01;
// TRACKING-SPEC-2026-10-01.md §4.6): target status, "not measured" text, minutes-since, presets (with Yesterday),
// the scoreboard column budget and the scoreboard CSV. No DOM. tests/trk-web.test.ts.
import type { ScoreMetrics, ScoreRow, Scoreboard, SetterTargets, TimelineEvent } from '../../../src/shared/api-tracking';
import type { ColumnSpec } from './layout';
import { toCsv } from './csv';
import { OPS_TZ, addDays, mondayOf } from './dates';

export const NOT_MEASURED = 'not measured';

// ---------- presets ----------

export type TrackPreset = 'today' | 'yesterday' | 'this_week' | 'last_week';
export const TRACK_PRESETS: Array<{ key: TrackPreset; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
];

export function trackPresetRange(p: TrackPreset, today: string): { from: string; to: string } {
  switch (p) {
    case 'today': return { from: today, to: today };
    case 'yesterday': { const y = addDays(today, -1); return { from: y, to: y }; }
    case 'this_week': return { from: mondayOf(today), to: today };
    case 'last_week': { const m = addDays(mondayOf(today), -7); return { from: m, to: addDays(m, 6) }; }
  }
}

// ---------- formatting ----------

/** Minutes as 'h:mm' ('0:45', '3:12'); null → 'not measured'. */
export function fmtHm(min: number | null | undefined): string {
  if (min === null || min === undefined || !Number.isFinite(min)) return NOT_MEASURED;
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

/** A number or 'not measured'. */
export const orNotMeasured = (v: number | null | undefined, fmt: (n: number) => string = (n) => n.toLocaleString()): string =>
  v === null || v === undefined || !Number.isFinite(v) ? NOT_MEASURED : fmt(v);

/** '7 min ago', '2 h 05 min ago', 'just now'; null → 'no dials yet'. */
export function minutesSince(iso: string | null | undefined, now: Date): string {
  if (!iso) return 'no dials yet';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'no dials yet';
  const m = Math.max(0, Math.floor((now.getTime() - t) / 60_000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min ago`;
}

const clockFmt = new Map<string, Intl.DateTimeFormat>();
/** Toronto wall clock 'HH:MM' (24 h). */
export function clock(iso: string | null | undefined, tz: string = OPS_TZ): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  let f = clockFmt.get(tz);
  if (!f) { f = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }); clockFmt.set(tz, f); }
  return f.format(d);
}

/** 'Oct 2, 14:00' in Toronto. */
export function dayClock(iso: string, tz: string = OPS_TZ): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(d)}, ${clock(iso, tz)}`;
}

export const pctText = (r: number | null | undefined, digits = 0): string =>
  r === null || r === undefined || !Number.isFinite(r) ? '—' : `${(r * 100).toFixed(digits)}%`;

// ---------- targets ----------

export type TargetStatus = 'met' | 'on_track' | 'open' | 'behind' | 'no_target';
export const TARGET_STATUS_LABEL: Record<TargetStatus, string> = { met: 'met', on_track: 'on track', open: 'in progress', behind: 'behind', no_target: 'no target' };

/**
 * actual vs target. `lowerIsBetter` for maxima (dials per meeting). `openShare` = share of the period already elapsed
 * when the period is still running (today, this week): below target but at or above target × share is "on track";
 * `openShare: null` on a running period with no schedule to pace against gives "in progress" instead of "behind".
 */
export function targetStatus(actual: number | null, target: number | null, o: { lowerIsBetter?: boolean; open?: boolean; openShare?: number | null } = {}): TargetStatus {
  if (target === null || target === undefined) return 'no_target';
  if (actual === null || actual === undefined || !Number.isFinite(actual)) return o.open ? 'open' : 'behind';
  if (o.lowerIsBetter) return actual <= target ? 'met' : 'behind';
  if (actual >= target) return 'met';
  if (o.open) {
    if (o.openShare === null || o.openShare === undefined) return 'open';
    return actual >= target * Math.max(0, Math.min(1, o.openShare)) ? 'on_track' : 'behind';
  }
  return 'behind';
}

/** Dials vs the daily target: a single day compares the day; a range compares the average per active day. */
export function dialsVsTarget(r: Pick<ScoreMetrics, 'dials' | 'activeDays'>, t: Pick<SetterTargets, 'dialsPerDay'>, singleDay: boolean): { text: string; value: number | null } {
  if (singleDay) return { text: t.dialsPerDay === null ? 'no target' : `${r.dials}/${t.dialsPerDay}`, value: r.dials };
  const avg = r.activeDays ? Math.round((r.dials / r.activeDays) * 10) / 10 : null;
  return { text: `${avg === null ? '—' : avg}/day${t.dialsPerDay === null ? '' : ` vs ${t.dialsPerDay}`}`, value: avg };
}

// ---------- flags ----------

export interface Flag { key: string; label: string; n: number; title: string }
export function flagsOf(r: ScoreMetrics): Flag[] {
  const f: Flag[] = [
    { key: 'skipped', label: 'skipped', n: r.servedNotDialled ?? 0, title: 'Leads served by Next that this setter did not dial within 15 min' },
    { key: 'blocked', label: 'blocked', n: r.gateBlocks, title: 'Dials stopped by the calling rules (gate blocks)' },
    { key: 'nodispo', label: 'no-dispo', n: r.undispositioned, title: 'Finished calls with no outcome logged after 15 min' },
    { key: 'suspect', label: 'suspect', n: r.noAnswerSuspects, title: 'Logged no answer, but the call was answered and lasted 30 s or more' },
    { key: 'cblate', label: 'callbacks late', n: r.callbacksOverdue, title: 'Open callbacks more than 15 min past due with no call since (as of now)' },
    { key: 'dnc', label: 'DNC', n: r.dncAdds, title: 'Businesses this setter added to do-not-call' },
  ];
  return f.filter((x) => x.n > 0);
}

// ---------- the scoreboard table (fixed layout, 981 px inside a card at 1280 px: admin/layout.ts tableBudget) ----------

export type TrackColumnKey = 'setter' | 'dials' | 'onDialer' | 'firstLast' | 'between' | 'connects' | 'convos' | 'booked' | 'show' | 'talk' | 'flags';
export const TRACK_COLUMNS: ReadonlyArray<ColumnSpec<TrackColumnKey>> = [
  { key: 'setter', label: 'Setter', width: 140, title: 'Below: practice average, last 30 days' },
  { key: 'dials', label: 'Dials', width: 90, num: true, title: 'Below: vs the daily dial target (a range: average per active day)' },
  { key: 'onDialer', label: 'On dialer', width: 90, num: true, title: 'Time on the dial screen (h:mm). Below: dials per hour on the dialer' },
  { key: 'firstLast', label: 'First–last', width: 90, title: 'First and last dial (Toronto). Ranges: active days below' },
  { key: 'between', label: 'Between calls', width: 80, num: true, title: 'Gaps of 20 min or more between calls. Below: longest gap' },
  { key: 'connects', label: 'Connects', width: 80, num: true, title: 'Below: connect rate (connects ÷ dials); answer rate in the tooltip' },
  { key: 'convos', label: 'Convos', width: 90, num: true, title: 'DM conversations. Below: connects of 60 s+ · 3 min+' },
  { key: 'booked', label: 'Booked', width: 70, num: true, title: 'Meetings booked. Below: set rate (booked ÷ DM conversations)' },
  { key: 'show', label: 'Show', width: 70, num: true, title: 'Show rate of meetings SCHEDULED in range. Below: outcomes not logged' },
  { key: 'talk', label: 'Talk', width: 80, num: true, title: 'Talk minutes on connects. Below: average wrap-up time' },
  { key: 'flags', label: 'Flags', width: 101, title: 'Only shown when not zero' },
];

// ---------- CSV ----------

export const SCOREBOARD_CSV_HEADER = [
  'setter', 'role', 'from', 'to', 'active_days', 'dials', 'dial_target_per_day', 'answered', 'answer_rate', 'connects', 'connect_rate',
  'dm_conversations', 'conv_60s', 'conv_3m', 'meetings_booked', 'set_rate', 'dials_per_meeting', 'meetings_held', 'meetings_no_show',
  'meetings_unlogged', 'show_rate', 'talk_minutes', 'line_minutes', 'avg_wrap_sec', 'on_dialer_minutes', 'dials_per_hour', 'leads_served',
  'served_not_dialled', 'vm_live', 'dnc_adds', 'gate_blocks', 'undispositioned', 'no_answer_suspects', 'callbacks_overdue',
  'gaps_over_10m', 'gaps_over_20m', 'longest_gap_min', 'gap_minutes_over_5m', 'first_dial_toronto', 'last_dial_toronto', 'practice_avg_30d',
];

function metricCells(m: ScoreMetrics, tz: string): Array<string | number | null> {
  return [
    m.activeDays, m.dials, null, m.answered, m.answerRate, m.connects, m.connectRate, m.dmConversations, m.conv60, m.conv3m,
    m.meetingsBooked, m.setRate, m.dialsPerMeeting, m.meetingsHeld, m.meetingsNoShow, m.meetingsUnlogged, m.showRate, m.talkMinutes,
    m.lineMinutes, m.avgWrapSec, m.onDialerMinutes, m.dialsPerHour, m.leadsServed, m.servedNotDialled, m.vmLive, m.dncAdds, m.gateBlocks,
    m.undispositioned, m.noAnswerSuspects, m.callbacksOverdue, m.gaps.over10, m.gaps.over20, m.gaps.longestMin, m.gaps.totalOver5Min,
    m.firstDialAt ? `${m.firstDialAt.slice(0, 10)} ${clock(m.firstDialAt, tz)}` : null, m.lastDialAt ? `${m.lastDialAt.slice(0, 10)} ${clock(m.lastDialAt, tz)}` : null,
  ];
}

/** Scoreboard CSV with a team row; formula-guarded. Blank cells are "not measured". */
export function scoreboardCsv(b: Scoreboard): string {
  const rows: Array<Array<string | number | null>> = [SCOREBOARD_CSV_HEADER];
  const withTarget = (cells: Array<string | number | null>, target: number | null) => { cells[2] = target; return cells; };
  for (const r of b.rows) rows.push([r.name, r.role, b.fromYmd, b.toYmd, ...withTarget(metricCells(r, b.tz), r.targets.dialsPerDay), r.practice?.avgScore30d ?? null]);
  rows.push(['Team', '', b.fromYmd, b.toYmd, ...metricCells(b.team, b.tz), null]);
  return toCsv(rows, { guardFormulas: true });
}

// ---------- timeline ----------

const DISPO_WORDS: Record<string, string> = {
  no_answer: 'no answer', vm_live: 'voicemail (live)', vm_dropped: 'voicemail (drop)', gatekeeper_intel: 'gatekeeper, intel',
  gatekeeper_blocked: 'gatekeeper, blocked', dm_not_interested: 'DM not interested', dm_later: 'DM later', dm_existing_solution: 'DM has a solution',
  callback_set: 'callback set', meeting_booked: 'meeting booked', wrong_number: 'wrong number', not_a_fit: 'not a fit', dnc_request: 'asked not to be called',
};
export const dispoWord = (d: string | null): string => (d ? DISPO_WORDS[d] ?? d.replace(/_/g, ' ') : 'no outcome logged');

const STATE_WORDS: Record<string, string> = { in_call: 'in a call', wrap: 'wrapping up', ready: 'on a lead', browsing: 'browsing', paused: 'on the picker', offline: 'offline' };
export const stateWord = (s: string): string => STATE_WORDS[s] ?? s;

/** One line per event: what happened and the detail. Pure. */
export function timelineLine(e: TimelineEvent): { what: string; detail: string } {
  switch (e.kind) {
    case 'login': return { what: 'Signed in', detail: '' };
    case 'presence': return { what: `Dial screen: ${stateWord(e.state)}`, detail: `${e.minutes} min` };
    case 'served': return { what: 'Lead served', detail: e.businessName ?? `lead ${e.membershipId}` };
    case 'opened': return { what: 'Lead opened', detail: e.businessName ?? `lead ${e.membershipId}` };
    case 'blocked': return { what: 'Dial blocked', detail: e.reasons.join(', ').replace(/_/g, ' ') };
    case 'call': {
      const bits = [e.businessName ?? 'unknown business', e.stateProv ?? '', e.answered ? `answered, ${e.durationSec ?? 0} s` : 'not answered', dispoWord(e.disposition)];
      if (e.wrapSec !== null) bits.push(`wrap ${e.wrapSec} s`);
      return { what: 'Call', detail: bits.filter(Boolean).join(' · ') };
    }
    case 'meeting': return { what: 'Meeting booked', detail: `${e.businessName ?? 'a business'} for ${dayClock(e.scheduledFor)} Toronto` };
    case 'dnc': return { what: 'Added to do-not-call', detail: e.businessName ?? '' };
    case 'gap': return { what: 'Time between calls', detail: `${e.minutes} min` };
  }
}

/** Disposition mix, most common first. Pure. */
export function dispositionMix(r: Pick<ScoreRow, 'dispositions'>): Array<{ code: string; label: string; n: number }> {
  return Object.entries(r.dispositions).filter(([, n]) => (n ?? 0) > 0)
    .map(([code, n]) => ({ code, label: dispoWord(code), n: n as number })).sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
}
